import { Controller } from '@hotwired/stimulus';

/**
 * Búsqueda + orden por columna para las tablas "historial" ya usadas en
 * varios lugares de la app (`.js-historial-table` / `.js-historial-search`,
 * ver register/fragments/_donacion_historial_popup.html.twig y hermanos) —
 * esas clases y los `data-sort-value` en cada <td> ya existían, pero sin
 * ningún JS detrás (búsqueda no filtraba nada, encabezados no ordenaban).
 * Este controller reutiliza esos mismos data-sort-value: no hace falta
 * tocar los templates que no lo necesiten, solo agregar
 * data-controller="historial-table" + los targets/actions donde sí.
 *
 * Uso:
 *   data-controller="historial-table"
 *   <input data-historial-table-target="search">
 *   <th data-historial-table-target="header" data-action="click->historial-table#sort">...</th>
 *   <td data-sort-value="...">...</td>  (misma posición de columna que el <th>)
 */
export default class extends Controller {

    static targets = ['search', 'header'];

    declare readonly searchTarget: HTMLInputElement;
    declare readonly hasSearchTarget: boolean;
    declare readonly headerTargets: HTMLElement[];

    private sortColumnIndex = -1;
    private sortAscending = true;

    connect(): void {
        if (this.hasSearchTarget) {
            this.searchTarget.addEventListener('input', this.filter);
        }
    }

    disconnect(): void {
        if (this.hasSearchTarget) {
            this.searchTarget.removeEventListener('input', this.filter);
        }
    }

    sort(event: Event): void {
        const header = event.currentTarget as HTMLElement;
        const index = this.headerTargets.indexOf(header);
        if (index === -1) return;

        this.sortAscending = this.sortColumnIndex === index ? !this.sortAscending : true;
        this.sortColumnIndex = index;

        this.headerTargets.forEach((h) => {
            h.classList.remove('historial-sort-asc', 'historial-sort-desc');
        });
        header.classList.add(this.sortAscending ? 'historial-sort-asc' : 'historial-sort-desc');

        const table = header.closest('table');
        const tbody = table?.querySelector('tbody');
        if (!tbody) return;

        const numeric = /^-?\d+(\.\d+)?$/;
        const direction = this.sortAscending ? 1 : -1;
        const rows = Array.from(tbody.querySelectorAll<HTMLTableRowElement>('tr'));
        rows.sort((a, b) => {
            const va = a.children[index]?.getAttribute('data-sort-value') ?? '';
            const vb = b.children[index]?.getAttribute('data-sort-value') ?? '';
            if (numeric.test(va) && numeric.test(vb)) {
                return (parseFloat(va) - parseFloat(vb)) * direction;
            }
            return va.localeCompare(vb) * direction;
        });
        rows.forEach((row) => tbody.appendChild(row));
    }

    private filter = (): void => {
        const query = this.normalize(this.searchTarget.value);
        const table = this.element.querySelector('table');
        const tbody = table?.querySelector('tbody');
        if (!tbody) return;

        tbody.querySelectorAll<HTMLTableRowElement>('tr').forEach((row) => {
            const text = this.normalize(row.textContent ?? '');
            row.style.display = query === '' || text.includes(query) ? '' : 'none';
        });
    };

    private normalize(text: string): string {
        return text.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim();
    }
}
